/**
 * ContractsPage
 *
 * Main page for listing and managing contracts.
 * Shows contracts with filtering by status and search.
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Description as TemplatesIcon,
  Visibility as ViewIcon,
  Send as SendIcon,
  NotificationsActive as RemindIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { contractsApi, Contract, ContractStatus } from '../../services/contractsApi';
import { getErrorMessage } from '../../services/api';
import { ContractsEmptyState } from '../../components/contracts/ContractsEmptyState';
import { ContractListSkeleton } from '../../components/contracts/ContractSkeleton';

// Status configuration
const STATUS_CONFIG: Record<ContractStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' }> = {
  draft: { label: 'Draft', color: 'default' },
  sent: { label: 'Sent', color: 'primary' },
  viewed: { label: 'Viewed', color: 'info' },
  signed: { label: 'Signed', color: 'secondary' },
  completed: { label: 'Completed', color: 'success' },
  declined: { label: 'Declined', color: 'error' },
  expired: { label: 'Expired', color: 'warning' },
  revoked: { label: 'Revoked', color: 'error' },
};

export default function ContractsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<ContractStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);

  // Fetch contracts
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['contracts', statusFilter, search, page, rowsPerPage],
    queryFn: () => contractsApi.list({
      status: statusFilter,
      search: search || undefined,
      page: page + 1,
      limit: rowsPerPage,
    }),
  });

  // Send reminder mutation
  const remindMutation = useMutation({
    mutationFn: (id: string) => contractsApi.remind(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
    },
  });

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, contract: Contract) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedContract(contract);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Handle view
  const handleView = () => {
    if (selectedContract) {
      navigate(`/contracts/${selectedContract._id}`);
    }
    handleMenuClose();
  };

  // Handle send (for drafts)
  const handleSend = () => {
    if (selectedContract) {
      navigate(`/contracts/${selectedContract._id}?action=send`);
    }
    handleMenuClose();
  };

  // Handle remind
  const handleRemind = () => {
    if (selectedContract) {
      remindMutation.mutate(selectedContract._id);
    }
    handleMenuClose();
  };

  // Handle download
  const handleDownload = async () => {
    if (selectedContract) {
      try {
        const blob = await contractsApi.download(selectedContract._id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedContract.contractNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Download failed:', err);
      }
    }
    handleMenuClose();
  };

  // Handle row click
  const handleRowClick = (contract: Contract) => {
    navigate(`/contracts/${contract._id}`);
  };

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={600}>
            Contracts
          </Typography>
          <Skeleton variant="rectangular" width={150} height={40} />
        </Box>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
        <ContractListSkeleton />
      </Box>
    );
  }

  // Error state - show a friendly message with context
  if (error) {
    const errorMessage = getErrorMessage(error);
    const isFeatureNotAvailable = errorMessage.includes('not available') || errorMessage.includes('not found');

    return (
      <Box sx={{ p: 3 }}>
        {/* Header - still show it for context */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={600}>
            Contracts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create and manage contracts with your clients
          </Typography>
        </Box>

        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: isFeatureNotAvailable ? 'grey.50' : 'error.lighter',
            border: '1px solid',
            borderColor: isFeatureNotAvailable ? 'grey.200' : 'error.light',
          }}
        >
          <Typography variant="h6" gutterBottom color={isFeatureNotAvailable ? 'text.primary' : 'error.main'}>
            {isFeatureNotAvailable ? 'Coming Soon' : 'Unable to Load Contracts'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
            {errorMessage}
          </Typography>
          {isFeatureNotAvailable && (
            <Typography variant="caption" color="text.secondary">
              The contracts feature allows you to send documents for e-signature with BoldSign integration.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  const contracts = data?.contracts || [];
  const pagination = data?.pagination || { total: 0, pages: 0 };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Contracts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Create and manage contracts with your clients
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<TemplatesIcon />}
            onClick={() => navigate('/contracts/templates')}
          >
            Templates
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/contracts/new')}
          >
            New Contract
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search contracts..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ width: 300 }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Status Tabs */}
      <Tabs
        value={statusFilter}
        onChange={(_, value) => {
          setStatusFilter(value);
          setPage(0);
        }}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="All" value="all" />
        <Tab label="Draft" value="draft" />
        <Tab label="Sent" value="sent" />
        <Tab label="Completed" value="completed" />
        <Tab label="Declined" value="declined" />
      </Tabs>

      {/* Empty State */}
      {contracts.length === 0 && !search && (
        <ContractsEmptyState onCreateClick={() => navigate('/contracts/new')} />
      )}

      {/* Search Empty State */}
      {contracts.length === 0 && search && (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="h6" gutterBottom>
            No contracts found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No contracts matching "{search}"
          </Typography>
        </Paper>
      )}

      {/* Contracts Table */}
      {contracts.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Contract</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map((contract) => (
                  <TableRow
                    key={contract._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(contract)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {contract.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contract.contractNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {contract.client ? (
                        <>
                          <Typography variant="body2">
                            {contract.client.firstName} {contract.client.lastName}
                          </Typography>
                          {contract.client.company && (
                            <Typography variant="caption" color="text.secondary">
                              {contract.client.company}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Unknown
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_CONFIG[contract.status]?.label || contract.status}
                        color={STATUS_CONFIG[contract.status]?.color || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {format(new Date(contract.createdAt), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      {format(new Date(contract.updatedAt), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, contract)}
                      >
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={pagination.total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleView}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>

        {selectedContract?.status === 'draft' && (
          <MenuItem onClick={handleSend}>
            <SendIcon fontSize="small" sx={{ mr: 1 }} />
            Send for Signature
          </MenuItem>
        )}

        {['sent', 'viewed'].includes(selectedContract?.status || '') && (
          <MenuItem onClick={handleRemind}>
            <RemindIcon fontSize="small" sx={{ mr: 1 }} />
            Send Reminder
          </MenuItem>
        )}

        {selectedContract?.boldsignDocumentId && (
          <MenuItem onClick={handleDownload}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            Download PDF
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
